import { IndexPageRedirect } from '@/components/IndexPageRedirect';
import { useActiveAutoLabelLibraryJob } from '@/hooks/api/taxonomy/useActiveAutoLabelLibraryJob';
import { useAutoLabelLibraryJob } from '@/hooks/api/taxonomy/useAutoLabelLibraryJob';
import { useDeleteTaxonomy } from '@/hooks/api/taxonomy/useDeleteTaxonomy';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useGetTenantTaxonomy } from '@/hooks/api/taxonomy/useGetTenantTaxonomy';
import { useUpdateTaxonomy } from '@/hooks/api/taxonomy/useUpdateTaxonomy';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSimpleModal } from '@/hooks/useModal';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Add,
  AutoAwesome,
  Check,
  Close,
  DeleteOutline,
  EditOutlined,
  FileUpload,
  LabelOffOutlined,
  AccountTreeOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabPanel, ViewTabs } from '@/components/ViewTabs';
import { AssetsWithoutLabelsView } from './AssetsWithoutLabelsView';
import { AutoLabelLibraryModal } from './AutoLabelLibraryModal';
import { CreateTaxonomyModal } from './CreateTaxonomyModal';
import { TaxonomyExportButton } from './TaxonomyExport/TaxonomyExportButton';
import { TaxonomyImportModal } from './TaxonomyImport/TaxonomyImportModal';
import { TaxonomyTree } from './TaxonomyTree';

export const TAXONOMY_VIEW_PARAM = 'view';
export const ASSETS_WITHOUT_LABELS_VIEW = 'assets-without-labels';
const TAXONOMY_VIEW = 'taxonomy';

const TAXONOMY_NAME_MAX_LENGTH = 64;

const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  padding: '24px',
});

// The panel the Taxonomy tab opens onto, so the tab reads as attached to what it selected rather
// than floating above a separate card.
const TaxonomyViewRoot = styled(TabPanel)({
  overflowY: 'auto',
  padding: '24px',
});

const Content = styled(Box)({
  width: '100%',
});

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '48px 24px',
});

export const TaxonomyPage = () => {
  usePageTitle('Taxonomy');

  const [searchParameters, setSearchParameters] = useSearchParams();
  const view =
    searchParameters.get(TAXONOMY_VIEW_PARAM) === ASSETS_WITHOUT_LABELS_VIEW
      ? ASSETS_WITHOUT_LABELS_VIEW
      : TAXONOMY_VIEW;

  const selectView = (nextView: string) => {
    const updatedParameters = new URLSearchParams(searchParameters);
    if (nextView === TAXONOMY_VIEW) {
      updatedParameters.delete(TAXONOMY_VIEW_PARAM);
    } else {
      updatedParameters.set(TAXONOMY_VIEW_PARAM, nextView);
    }
    setSearchParameters(updatedParameters);
  };

  const { permissions } = useGetCurrentUser();
  const { isTaxonomyAiClassificationEnabled } = useFeatureFlags();
  const { taxonomy, isLoading } = useGetTenantTaxonomy();
  const createModal = useSimpleModal();
  const importModal = useSimpleModal();
  const deleteModal = useSimpleModal();
  const autoLabelModal = useSimpleModal();
  const { deleteTaxonomy, isDeleting, deleteError } = useDeleteTaxonomy();
  const { updateTaxonomy, isUpdating, updateError } = useUpdateTaxonomy(taxonomy?.id ?? '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const renameContainerReference = useRef<HTMLDivElement>(null);

  const startEditingName = () => {
    if (!taxonomy) return;
    setDraftName(taxonomy.name);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setDraftName('');
  };

  const submitName = () => {
    if (!taxonomy) return;
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === taxonomy.name) {
      cancelEditingName();
      return;
    }
    updateTaxonomy(
      { name: trimmed },
      {
        onSuccess: () => {
          setIsEditingName(false);
          setDraftName('');
        },
      }
    );
  };
  const [kickedOffJobId, setKickedOffJobId] = useState<string | undefined>();
  const [dismissedTerminalJobId, setDismissedTerminalJobId] = useState<string | undefined>();
  const { activeJob: pendingJobOnMount } = useActiveAutoLabelLibraryJob(taxonomy?.id);
  const candidateJobId = kickedOffJobId ?? pendingJobOnMount?.id;
  const trackedJobId = candidateJobId === dismissedTerminalJobId ? undefined : candidateJobId;
  const { job: trackedJob, notFound: trackedJobNotFound } = useAutoLabelLibraryJob(taxonomy?.id, trackedJobId);
  const isTerminal =
    trackedJob?.status === 'COMPLETED' || trackedJob?.status === 'FAILED' || trackedJob?.status === 'CANCELLED';
  const trackedJobForModal = trackedJobNotFound ? undefined : trackedJob;

  const dismissTerminalJob = () => {
    if (trackedJob) {
      setDismissedTerminalJobId(trackedJob.id);
      setKickedOffJobId(undefined);
    }
  };

  const handleAutoLabelModalClose = () => {
    if (isTerminal) {
      dismissTerminalJob();
    }
    autoLabelModal.close();
  };

  if (!permissions.canManageTaxonomy) {
    return <IndexPageRedirect />;
  }

  if (isLoading) {
    return (
      <PageRoot>
        <CircularProgress />
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <ViewTabs value={view} onChange={(_, nextView) => selectView(nextView)}>
        <Tab icon={<AccountTreeOutlined />} iconPosition='start' label='Taxonomy' value={TAXONOMY_VIEW} />
        <Tab
          icon={<LabelOffOutlined />}
          iconPosition='start'
          label='Assets without labels'
          value={ASSETS_WITHOUT_LABELS_VIEW}
        />
      </ViewTabs>
      {view === ASSETS_WITHOUT_LABELS_VIEW && (
        <AssetsWithoutLabelsView onBuildTaxonomy={() => selectView(TAXONOMY_VIEW)} />
      )}
      {view === TAXONOMY_VIEW && (
        <TaxonomyViewRoot>
          <Content>
            {taxonomy ? (
              taxonomy && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        minHeight: 32,
                      }}
                    >
                      {isEditingName ? (
                        <Box
                          ref={renameContainerReference}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1, minWidth: 0 }}
                        >
                          <TextField
                            size='small'
                            value={draftName}
                            onChange={event => setDraftName(event.target.value)}
                            onKeyDown={event => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                submitName();
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelEditingName();
                              }
                            }}
                            onBlur={event => {
                              if (!renameContainerReference.current?.contains(event.relatedTarget as Node)) {
                                cancelEditingName();
                              }
                            }}
                            autoFocus
                            disabled={isUpdating}
                            inputProps={{
                              'aria-label': 'Taxonomy name',
                              maxLength: TAXONOMY_NAME_MAX_LENGTH,
                              sx: (theme: Theme) => ({
                                ...theme.typography.h6,
                                py: 0,
                                height: 32,
                              }),
                            }}
                            slotProps={{
                              input: {
                                endAdornment: isUpdating ? <CircularProgress size={16} /> : undefined,
                                sx: { height: 32, py: 0 },
                              },
                            }}
                            sx={{ flexGrow: 1, maxWidth: 360 }}
                            data-testid='taxonomy-name-input'
                          />
                          <IconButton
                            size='small'
                            onClick={submitName}
                            disabled={isUpdating || !draftName.trim() || draftName.trim() === taxonomy.name}
                            title='Save'
                            aria-label='Save taxonomy name'
                          >
                            <Check sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={cancelEditingName}
                            disabled={isUpdating}
                            title='Cancel'
                            aria-label='Cancel renaming taxonomy'
                          >
                            <Close sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            '&:hover .taxonomy-rename-toggle, &:focus-within .taxonomy-rename-toggle': {
                              opacity: 1,
                            },
                          }}
                        >
                          <Typography variant='h6' color='text.primary' noWrap>
                            {taxonomy.name}
                          </Typography>
                          <Tooltip title='Rename'>
                            <IconButton
                              size='small'
                              onClick={startEditingName}
                              aria-label='Rename taxonomy'
                              data-testid='taxonomy-rename-button'
                              className='taxonomy-rename-toggle'
                              sx={{
                                opacity: 0,
                                transition: 'opacity 120ms ease-in-out',
                                '&:focus-visible': { opacity: 1 },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: '18px' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {isTaxonomyAiClassificationEnabled && (
                          <Tooltip title='Auto-label options'>
                            <IconButton
                              size='small'
                              onClick={autoLabelModal.open}
                              aria-label='Auto-label options'
                              data-testid='taxonomy-auto-label-library-button'
                            >
                              <AutoAwesome sx={{ fontSize: '18px' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title='Reimport'>
                          <IconButton size='small' onClick={importModal.open} aria-label='Reimport'>
                            <FileUpload sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Tooltip>
                        <TaxonomyExportButton taxonomyId={taxonomy.id} />
                        <Tooltip title='Delete'>
                          <IconButton size='small' color='error' onClick={deleteModal.open}>
                            <DeleteOutline sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    {updateError && (
                      <Alert severity='error' sx={{ mt: 1 }}>
                        {updateError.message}
                      </Alert>
                    )}
                    <Box component='ul' sx={{ mt: 0.5, mb: 0, pl: 2.5, '& li': { mb: 0.25 } }}>
                      <Typography component='li' variant='body2' color='text.secondary'>
                        Add top-level categories, then nest subcategories to build your hierarchy
                      </Typography>
                      <Typography component='li' variant='body2' color='text.secondary'>
                        The most specific categories (labels) are what assets get assigned to
                      </Typography>
                      <Typography component='li' variant='body2' color='text.secondary'>
                        Assets can be categorized automatically, manually, or both
                      </Typography>
                    </Box>
                  </Box>
                  <TaxonomyTree taxonomyId={taxonomy.id} />
                </Box>
              )
            ) : (
              <EmptyState>
                <Typography variant='h6' color='text.primary'>
                  No taxonomy yet
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Create a taxonomy to organize your assets into hierarchical categories.
                </Typography>
                <Button variant='contained' startIcon={<Add />} onClick={createModal.open}>
                  Create Taxonomy
                </Button>
              </EmptyState>
            )}
          </Content>
        </TaxonomyViewRoot>
      )}
      <CreateTaxonomyModal isOpen={createModal.isOpen} onClose={createModal.close} />
      {taxonomy && (
        <>
          <TaxonomyImportModal isOpen={importModal.isOpen} onClose={importModal.close} taxonomyId={taxonomy.id} />
          <AutoLabelLibraryModal
            isOpen={autoLabelModal.isOpen}
            onClose={handleAutoLabelModalClose}
            onJobStarted={setKickedOffJobId}
            onDismissTerminal={dismissTerminalJob}
            taxonomyId={taxonomy.id}
            autoLabelOptedIn={taxonomy.autoLabelOptedIn}
            job={trackedJobForModal}
          />
          <Dialog
            open={deleteModal.isOpen}
            onClose={isDeleting ? undefined : deleteModal.close}
            slotProps={{ paper: { sx: { borderRadius: '12px', width: '405px' } } }}
          >
            <DialogTitle>Delete Taxonomy</DialogTitle>
            <DialogContent>
              {deleteError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {deleteError.message}
                </Alert>
              )}
              <Typography variant='body2'>
                This will permanently delete the taxonomy and all its categories. This action cannot be undone.
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
                If any assets are currently assigned to categories, you must remove those assignments first before the
                taxonomy can be deleted.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={deleteModal.close} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='error'
                disabled={isDeleting}
                sx={{ width: '100px' }}
                onClick={() =>
                  deleteTaxonomy(taxonomy.id, {
                    onSuccess: deleteModal.close,
                  })
                }
              >
                {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </PageRoot>
  );
};
